
import { useState } from 'react';

interface SidebarProps {
  currentUser: {
    id: string;
    name: string;
    role: 'admin' | 'agent' | 'super_admin' | 'call_center';
    company_id?: string;
    company_name?: string;
    company?: {
      id: string;
      name: string;
      logo?: string;
    };
  };
  currentPage: string;
  onNavigate: (page: string) => void;
  onLogout: () => void;
}

export default function Sidebar({ currentUser, currentPage, onNavigate, onLogout }: SidebarProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Menu spécial pour Super Admin
  const superAdminMenuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-line' },
    { id: 'companies', label: 'Sociétés', icon: 'ri-building-line' },
  ];

  // Menu normal pour Admin/Agent/Call Center
  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: 'ri-dashboard-line' },
    { id: 'clients', label: 'Clients', icon: 'ri-group-line' },
    { id: 'pipeline', label: 'Pipeline', icon: 'ri-flow-chart' },
    { id: 'planning', label: 'Planning', icon: 'ri-calendar-line' },
    ...(currentUser.role === 'admin' ? [
      { id: 'users', label: 'Utilisateurs', icon: 'ri-user-settings-line' },
      { id: 'settings', label: 'Paramètres', icon: 'ri-settings-line' }
    ] : [])
  ];

  const items = currentUser.role === 'super_admin' ? superAdminMenuItems : menuItems;

  // Obtenir le nom de la société à afficher
  const getCompanyDisplayName = () => {
    if (currentUser.role === 'super_admin') {
      return 'Super Admin Panel';
    }
    return currentUser.company?.name || currentUser.company_name || 'CRM Pro';
  };

  // Obtenir le logo ou l'icône par défaut
  const getCompanyLogo = () => {
    if (currentUser.role === 'super_admin') {
      return (
        <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mr-3">
          <i className="ri-vip-crown-line text-white text-lg"></i>
        </div>
      );
    }

    if (currentUser.company?.logo) {
      return (
        <div className="w-10 h-10 rounded-full mr-3 overflow-hidden bg-gray-100 flex items-center justify-center">
          <img 
            src={currentUser.company.logo} 
            alt={`Logo ${currentUser.company.name}`}
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    return (
      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
        <i className="ri-building-line text-white text-lg"></i>
      </div>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border"
      >
        <i className={`${isOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-gray-600`}></i>
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out flex flex-col`}>
        
        {/* Header avec logo et nom de la société */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center">
            {getCompanyLogo()}
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-gray-900 text-sm truncate">
                {getCompanyDisplayName()}
              </h2>
              <p className="text-xs text-gray-500">
                {currentUser.role === 'super_admin' ? 'Système Multi-Sociétés' : 'CRM Commercial'}
              </p>
            </div>
          </div>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
              currentUser.role === 'super_admin' ? 'bg-purple-100' :
              currentUser.role === 'admin' ? 'bg-blue-100' : 
              currentUser.role === 'call_center' ? 'bg-orange-100' :
              'bg-green-100'
            }`}>
              <i className={`${
                currentUser.role === 'super_admin' ? 'ri-vip-crown-line text-purple-600' :
                currentUser.role === 'admin' ? 'ri-shield-user-line text-blue-600' : 
                currentUser.role === 'call_center' ? 'ri-headphone-line text-orange-600' :
                'ri-user-line text-green-600'
              } text-sm`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{currentUser.name}</p>
              <p className="text-xs text-gray-500 truncate">
                {currentUser.role === 'super_admin' ? 'Super Administrateur' :
                 currentUser.role === 'admin' ? 'Administrateur' : 
                 currentUser.role === 'call_center' ? 'Call Center' :
                 'Agent commercial'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => {
                    onNavigate(item.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    currentPage === item.id
                      ? currentUser.role === 'super_admin' 
                        ? 'bg-purple-100 text-purple-900' 
                        : 'bg-blue-100 text-blue-900'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <i className={`${item.icon} mr-3 text-lg`}></i>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg transition-colors"
          >
            <i className="ri-logout-box-line mr-3 text-lg"></i>
            Déconnexion
          </button>
        </div>
      </div>
    </>
  );
}
